import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Tiered free-gift picker for the cart (drawer + page).
 *
 * - Tier 1 ($100): customer taps a tile to choose one gift (towel/cylinder). Choosing a
 *   second one swaps (enforcement keeps only the newest tier-1 gift).
 * - Tier 2 ($150): the plushie is AUTO-added (like free shipping) and re-adds if removed.
 * - Any gift is auto-removed the moment the cart drops below its threshold, or when there
 *   are no paid items (cart.total_price is $0 because gifts themselves are $0).
 *
 * Gifts are added as their $0 companion variant (a fixed bundle of 1x the real stock),
 * with a _gift property. State lives in Liquid; this only mutates the cart + lets
 * <cart-items-component> morph the drawer to re-render.
 */

const GIFT_PROPERTY = '_gift';

const CART_ROOT = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const cartUrl = () => `${CART_ROOT()}cart.js`;
const cartAddUrl = () => `${CART_ROOT()}cart/add.js`;
const cartChangeUrl = () => `${CART_ROOT()}cart/change.js`;
const cartUpdateUrl = () => `${CART_ROOT()}cart/update.js`;

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((c) => {
    if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

function parseIds(str) {
  return (str || '')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n));
}

/** Tier config from the rendered component (present whenever the meter shows). */
function giftConfig() {
  const el = document.querySelector('gift-rewards-component');
  if (!el) return null;
  return {
    t1: parseInt(el.dataset.t1Amt || '0', 10),
    t2: parseInt(el.dataset.t2Amt || '0', 10),
    t1Variants: parseIds(el.dataset.t1Variants),
    t2Variants: parseIds(el.dataset.t2Variants),
  };
}

class GiftRewardsComponent extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  #onClick = (event) => {
    const tile = event.target instanceof Element ? event.target.closest('[data-gr-tile]') : null;
    if (!tile || this.#busy || tile.hasAttribute('disabled')) return;
    event.preventDefault();
    if (tile.classList.contains('is-selected')) {
      this.#remove(tile.getAttribute('data-line-key'), tile);
      return;
    }
    // If a gift in the same tier is already chosen, SWAP in one request (remove the old +
    // add the new together) so it's an instant switch, not add-then-trim.
    const chosen = this.querySelector('.dgt-gr__tile.is-selected[data-variant-id]');
    if (chosen && chosen !== tile && chosen.getAttribute('data-tier') === tile.getAttribute('data-tier')) {
      this.#swap(chosen.getAttribute('data-variant-id'), tile.getAttribute('data-variant-id'), chosen, tile);
    } else {
      this.#add(tile.getAttribute('data-variant-id'), tile);
    }
  };

  #setBusy(state) {
    this.#busy = state;
    this.classList.toggle('is-busy', state);
  }

  async #add(variantId, tile) {
    if (!variantId) return;
    this.#setBusy(true);
    if (tile) tile.classList.add('is-selected'); // optimistic
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append(`properties[${GIFT_PROPERTY}]`, 'true');
    body.append('sections', cartSectionIds());
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (data && data.status) {
        if (tile) tile.classList.remove('is-selected'); // add failed (sold out)
        return;
      }
      this.dispatchEvent(new CartAddEvent({}, this.sectionId, { source: 'dgt-gift-picker', sections: data.sections }));
    } catch (error) {
      if (tile) tile.classList.remove('is-selected');
      console.error('[dgt-gift-picker] add failed', error);
    } finally {
      this.#setBusy(false);
    }
  }

  async #remove(lineKey, tile) {
    if (!lineKey) return;
    this.#setBusy(true);
    if (tile) tile.classList.remove('is-selected'); // optimistic
    try {
      const res = await fetch(cartChangeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/html' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: cartSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      this.dispatchEvent(new CartUpdateEvent(data, this.sectionId, { source: 'dgt-gift-picker', sections: data.sections }));
    } catch (error) {
      if (tile) tile.classList.add('is-selected');
      console.error('[dgt-gift-picker] remove failed', error);
    } finally {
      this.#setBusy(false);
    }
  }

  /** One-request swap: remove the old gift + add the new one together (instant switch). */
  async #swap(oldVariantId, newVariantId, oldTile, newTile) {
    if (!oldVariantId || !newVariantId) return;
    this.#setBusy(true);
    if (oldTile) oldTile.classList.remove('is-selected'); // optimistic
    if (newTile) newTile.classList.add('is-selected');
    const updates = {};
    updates[oldVariantId] = 0;
    updates[newVariantId] = 1;
    try {
      const res = await fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/html' },
        body: JSON.stringify({ updates, sections: cartSectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      this.dispatchEvent(new CartUpdateEvent(data, this.sectionId, { source: 'dgt-gift-picker', sections: data.sections }));
    } catch (error) {
      if (oldTile) oldTile.classList.add('is-selected'); // revert
      if (newTile) newTile.classList.remove('is-selected');
      console.error('[dgt-gift-picker] swap failed', error);
    } finally {
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('gift-rewards-component')) {
  customElements.define('gift-rewards-component', GiftRewardsComponent);
}

/* ---------- Enforcement: auto-add plushie, remove gifts below threshold, keep 1 tier-1 ---------- */

function usableCart(cart) {
  return cart && Array.isArray(cart.items) && typeof cart.total_price === 'number' ? cart : null;
}

let working = false;
async function enforceGifts(seedCart) {
  if (working) return;
  const cfg = giftConfig();
  if (!cfg) return;
  working = true;
  try {
    const cart = usableCart(seedCart) || (await (await fetch(cartUrl(), { cache: 'no-store' })).json());
    const items = cart.items || [];
    const current = cart.total_price; // gifts are $0, so this is the paid subtotal
    const isT1 = (i) => cfg.t1Variants.includes(i.variant_id);
    const isT2 = (i) => cfg.t2Variants.includes(i.variant_id);
    const t1Lines = items.filter(isT1);
    const t2Lines = items.filter(isT2);

    const updates = {};
    let addPlushie = null;

    // Tier 1 ($100 gift): at most ONE line at quantity 1, and only at/above $100.
    if (current < cfg.t1) {
      t1Lines.forEach((i) => { updates[i.key] = 0; }); // dropped below $100 → remove all
    } else {
      t1Lines.forEach((i, idx) => {
        if (idx < t1Lines.length - 1) updates[i.key] = 0;      // remove older duplicate lines
        else if (i.quantity > 1) updates[i.key] = 1;           // cap the kept one to qty 1
      });
    }

    // Tier 2 ($150 plushie — auto): at most ONE line at quantity 1.
    if (current < cfg.t2) {
      t2Lines.forEach((i) => { updates[i.key] = 0; }); // dropped below $150 → remove
    } else if (t2Lines.length === 0 && cfg.t2Variants.length) {
      addPlushie = cfg.t2Variants[0]; // at/over $150, none present → auto-add
    } else {
      t2Lines.forEach((i, idx) => {
        if (idx < t2Lines.length - 1) updates[i.key] = 0;
        else if (i.quantity > 1) updates[i.key] = 1;
      });
    }

    if (!Object.keys(updates).length && !addPlushie) return;

    if (Object.keys(updates).length) {
      const res = await fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/html' },
        body: JSON.stringify({ updates, sections: cartSectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      document.dispatchEvent(new CartUpdateEvent(data, '', { source: 'dgt-gift-trim', sections: data.sections }));
    }
    if (addPlushie) {
      const body = new FormData();
      body.append('id', String(addPlushie));
      body.append('quantity', '1');
      body.append(`properties[${GIFT_PROPERTY}]`, 'true');
      body.append('sections', cartSectionIds());
      body.append('sections_url', window.location.pathname);
      const res = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!(data && data.status)) {
        document.dispatchEvent(new CartUpdateEvent({}, '', { source: 'dgt-gift-add', sections: data.sections }));
      }
    }
  } catch (error) {
    console.error('[dgt-gift-picker] enforce failed', error);
  } finally {
    working = false;
  }
}

// Our own trim/add re-dispatches cart:update; the `working` guard makes re-entry a no-op,
// and enforcement converges (each pass either changes nothing or moves toward the stable state).
document.addEventListener('cart:update', (event) => {
  enforceGifts(event?.detail?.resource);
});

function boot() {
  enforceGifts();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
